<!--
========================================================================
🔷 MODAL DE REINTENTO POR FALTA DE CONEXIÓN
   💡 Se muestra cuando una acción no puede completarse por falta de red.
   💡 El botón "Reintentar" vuelve a ejecutar la acción pendiente gestionada por `retryManager`.
   💡 Muestra spinner si está reintentando, y lo oculta al finalizar.
========================================================================
-->
<template>
  <ion-modal :is-open="true" backdrop-dismiss="false">
    <!-- 🔺 CABECERA CON AVISO -->
    <ion-header>
      <ion-toolbar color="danger">
        <ion-title class="centered-title">🔌Sin conexión a internet</ion-title>
      </ion-toolbar>
    </ion-header>

    <!-- 📦 CONTENIDO CON MENSAJE Y BOTÓN -->
    <ion-content class="ion-padding">
      <p class="text-center">
        No se pudo completar una acción porque no se ha detectado una conexión a
        internet activa.
      </p>
      <p class="text-center">
        Por favor revisa tu configuración y pulsa
        <strong>Reintentar</strong> cuando se haya restablecido.
      </p>

      <!-- 🔁 BOTÓN DE REINTENTO -->
      <ion-button expand="block" :disabled="isRetrying" @click="retry">
        <!-- ⏳ Spinner mientras se reintenta -->
        <ion-spinner v-if="isRetrying" name="dots" class="me-2" />
        <!-- 🔁 Texto alternativo cuando no está cargando -->
        <span v-else>🔁 Reintentar</span>
      </ion-button>
    </ion-content>
  </ion-modal>
</template>

<!--
========================================================================
🔧 SCRIPT SETUP
   💡 Se importa el estado `isRetrying` del retryManager para controlar el spinner y desactivar el botón.
========================================================================
-->
<script setup lang="ts">
import { retryManager } from "@/composables/useRetryManager";
import { onMounted, onBeforeUnmount } from "vue";

import {
  IonTitle,
  IonHeader,
  IonToolbar,
  IonContent,
  IonSpinner,
  IonButton,
  IonModal,
} from "@ionic/vue";

const { isRetrying, retry, getPendingAction } = retryManager;

let autoRetryInterval: NodeJS.Timeout | null = null;

// ⏱️ Cada 30s ejecutamos el reintento automáticamente (si no está ya reintentando)
onMounted(() => {
  autoRetryInterval = setInterval(() => {
    if (
      retryManager.showRetryModal.value &&
      !retryManager.isRetrying.value &&
      retryManager.getPendingAction().value
    ) {
      console.log("🔁 Reintentando automáticamente...");
      retry();
    }
  }, 10000); // cada 10 segundos
});

onBeforeUnmount(() => {
  if (autoRetryInterval) clearInterval(autoRetryInterval);
});
</script>

<!--
========================================================================
🎨 ESTILOS
   💡 Centra visualmente el título del modal
========================================================================
-->
<style scoped>
.centered-title {
  text-align: center;
  justify-content: center;
  display: flex;
}
</style>
